import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  Attachment,
  AttachmentKind,
  AttachmentList,
} from "@/api/types";
import { tokenStorage } from "@/auth/tokenStorage";
import { env } from "@/lib/env";

const KEY_THREAD = (threadId: string) =>
  ["thread-attachments", threadId] as const;
const KEY_ITEM = (attachmentId: string) =>
  ["attachment", attachmentId] as const;

// --- list / item ----------------------------------------------------------

export function useThreadAttachments(
  threadId: string | undefined,
  kind: AttachmentKind | "all" = "all",
) {
  return useQuery({
    queryKey: threadId
      ? [...KEY_THREAD(threadId), kind]
      : ["thread-attachments", "noop"],
    queryFn: () =>
      api.GET<AttachmentList>(`/threads/${threadId}/attachments`, {
        params: { kind },
      }),
    enabled: Boolean(threadId),
  });
}

/**
 * Polling de um anexo individual. Quando o status final
 * (``done``/``failed``/``not_needed``) eh atingido, o ``refetchInterval``
 * desativa.
 */
export function useAttachment(
  attachmentId: string | undefined,
  options: { poll?: boolean } = {},
) {
  return useQuery<Attachment>({
    queryKey: attachmentId
      ? KEY_ITEM(attachmentId)
      : ["attachment", "noop"],
    queryFn: () => api.GET<Attachment>(`/attachments/${attachmentId}`),
    enabled: Boolean(attachmentId),
    refetchInterval: (query) => {
      if (!options.poll) return false;
      const data = query.state.data;
      if (!data) return 1500;
      const terminal =
        data.digestion_status === "done" ||
        data.digestion_status === "failed" ||
        data.digestion_status === "not_needed";
      return terminal ? false : 1500;
    },
  });
}

// --- delete ---------------------------------------------------------------

export function useDeleteAttachment(threadId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      api.DELETE<void>(`/attachments/${attachmentId}`),
    onSuccess: () => {
      if (threadId)
        void qc.invalidateQueries({ queryKey: KEY_THREAD(threadId) });
    },
  });
}

// --- upload (XHR com progress) -------------------------------------------

export interface UploadAttachmentResult {
  status: number;
  body: Attachment | { error: { code: string; message: string } } | unknown;
}

export async function uploadAttachmentXhr(
  threadId: string,
  file: File,
  options: {
    onProgress?: (pct: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<UploadAttachmentResult> {
  // Resolve o token ANTES de abrir o XHR para nao precisar de async no
  // executor do Promise (no-async-promise-executor).
  const token = await tokenStorage.getToken();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = `${env.API_BASE_URL}/threads/${threadId}/attachments`;
    const form = new FormData();
    form.append("file", file);

    xhr.open("POST", url);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && options.onProgress) {
        options.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status, body });
      } else {
        reject({ status: xhr.status, body });
      }
    };
    xhr.onerror = () => reject({ status: 0, body: "network_error" });
    xhr.onabort = () => reject({ status: 0, body: "aborted" });

    if (options.signal) {
      if (options.signal.aborted) xhr.abort();
      else options.signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.send(form);
  });
}

/**
 * Faz download de um anexo via API com auth header e dispara save no
 * navegador. Como o bucket eh privado, NAO usamos ``<a href>`` direto.
 */
export async function downloadAttachment(
  attachmentId: string,
  filename: string,
): Promise<void> {
  const token = await tokenStorage.getToken();
  const resp = await fetch(
    `${env.API_BASE_URL}/attachments/${attachmentId}/download`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!resp.ok) {
    throw new Error(`Download falhou: HTTP ${resp.status}`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
